/**
 * RBAC (Roles & Permissions) Page - Company Admin
 */
import { useState, useEffect } from 'react';
import { useOutletContext } from 'react-router-dom';
import { UserCog, Plus, Edit, Trash2, Shield, Check, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { toast } from 'sonner';
import * as api from '@/lib/companyAdminApi';

export default function RBACPage() {
  const { token } = useOutletContext();
  const [roles, setRoles] = useState([]);
  const [permissions, setPermissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showRoleDialog, setShowRoleDialog] = useState(false);
  const [editingRole, setEditingRole] = useState(null);
  const [selectedRole, setSelectedRole] = useState(null);
  const [rolePermissions, setRolePermissions] = useState([]);
  const [roleForm, setRoleForm] = useState({ name: '', description: '' });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [rolesRes, permsRes] = await Promise.all([
        api.getRoles(),
        api.getPermissions(),
      ]);
      setRoles(rolesRes.data || []);
      setPermissions(permsRes.data || []);
    } catch (error) {
      toast.error('Failed to load RBAC data');
    } finally {
      setLoading(false);
    }
  };

  const handleSaveRole = async () => {
    try {
      if (editingRole) {
        await api.updateRole(editingRole.id, roleForm);
        toast.success('Role updated');
      } else {
        await api.createRole(roleForm);
        toast.success('Role created');
      }
      setShowRoleDialog(false);
      resetRoleForm();
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save role');
    }
  };

  const handleDeleteRole = async (id) => {
    if (!window.confirm('Are you sure you want to delete this role?')) return;
    try {
      await api.deleteRole(id);
      toast.success('Role deleted');
      fetchData();
    } catch (error) {
      toast.error('Failed to delete role');
    }
  };

  const openEditDialog = (role) => {
    setEditingRole(role);
    setRoleForm({ name: role.name, description: role.description || '' });
    setShowRoleDialog(true);
  };

  const resetRoleForm = () => {
    setEditingRole(null);
    setRoleForm({ name: '', description: '' });
  };

  const selectRole = (role) => {
    setSelectedRole(role);
    setRolePermissions(role.permissions?.map(p => p.id) || []);
  };

  const togglePermission = (permId) => {
    setRolePermissions(prev => 
      prev.includes(permId) 
        ? prev.filter(id => id !== permId)
        : [...prev, permId]
    );
  };

  const saveRolePermissions = async () => {
    if (!selectedRole) return;
    try {
      await api.assignRolePermissions(selectedRole.id, rolePermissions);
      toast.success('Permissions updated');
      fetchData();
    } catch (error) {
      toast.error('Failed to update permissions');
    }
  };

  // Group permissions by category
  const groupedPermissions = permissions.reduce((acc, perm) => {
    const category = perm.perm_key.split('.')[1] || 'other';
    if (!acc[category]) acc[category] = [];
    acc[category].push(perm);
    return acc;
  }, {});

  return (
    <div className="space-y-6" data-testid="rbac-page">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Roles & Permissions</h1>
          <p className="text-gray-500">Manage admin access control</p>
        </div>
        <Button onClick={() => { resetRoleForm(); setShowRoleDialog(true); }} data-testid="create-role-btn">
          <Plus className="h-4 w-4 mr-2" />
          New Role
        </Button>
      </div>

      <Tabs defaultValue="roles" className="space-y-4">
        <TabsList>
          <TabsTrigger value="roles">Roles</TabsTrigger>
          <TabsTrigger value="permissions">Permissions Matrix</TabsTrigger>
        </TabsList>

        {/* Roles Tab */}
        <TabsContent value="roles">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Roles List */}
            <Card>
              <CardHeader>
                <CardTitle>Roles</CardTitle>
                <CardDescription>Click a role to manage its permissions</CardDescription>
              </CardHeader>
              <CardContent className="space-y-2">
                {loading ? (
                  <div className="text-center py-8 text-gray-500">Loading...</div>
                ) : roles.length === 0 ? (
                  <div className="text-center py-8 text-gray-500">No roles found</div>
                ) : (
                  roles.map((role) => (
                    <div
                      key={role.id}
                      onClick={() => selectRole(role)}
                      className={`p-4 rounded-lg border cursor-pointer transition-colors ${
                        selectedRole?.id === role.id ? 'border-blue-500 bg-blue-50' : 'hover:bg-gray-50'
                      }`}
                    >
                      <div className="flex justify-between items-start">
                        <div>
                          <h3 className="font-medium flex items-center gap-2">
                            <Shield className="h-4 w-4 text-blue-500" />
                            {role.name}
                          </h3>
                          <p className="text-sm text-gray-500 mt-1">{role.description || 'No description'}</p>
                          <p className="text-xs text-gray-400 mt-2">
                            {role.permissions?.length || 0} permissions
                          </p>
                        </div>
                        <div className="flex gap-2">
                          <Button size="sm" variant="ghost" onClick={(e) => { e.stopPropagation(); openEditDialog(role); }}>
                            <Edit className="h-4 w-4" />
                          </Button>
                          <Button size="sm" variant="ghost" className="text-red-600" onClick={(e) => { e.stopPropagation(); handleDeleteRole(role.id); }}>
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </div>
                    </div>
                  ))
                )}
              </CardContent>
            </Card>

            {/* Permission Assignment */}
            <Card>
              <CardHeader>
                <CardTitle>
                  {selectedRole ? `Permissions: ${selectedRole.name}` : 'Select a Role'}
                </CardTitle>
              </CardHeader>
              <CardContent>
                {selectedRole ? (
                  <div className="space-y-4">
                    <div className="max-h-[400px] overflow-y-auto space-y-4">
                      {Object.entries(groupedPermissions).map(([category, perms]) => (
                        <div key={category}>
                          <h4 className="text-sm font-medium text-gray-700 capitalize mb-2">{category}</h4>
                          <div className="space-y-2 pl-2">
                            {perms.map((perm) => (
                              <label key={perm.id} className="flex items-center gap-3 cursor-pointer">
                                <Checkbox
                                  checked={rolePermissions.includes(perm.id)}
                                  onCheckedChange={() => togglePermission(perm.id)}
                                />
                                <div>
                                  <p className="text-sm font-medium">{perm.perm_key}</p>
                                  <p className="text-xs text-gray-500">{perm.description}</p>
                                </div>
                              </label>
                            ))}
                          </div>
                        </div>
                      ))}
                    </div>
                    <Button onClick={saveRolePermissions} className="w-full">
                      Save Permissions
                    </Button>
                  </div>
                ) : (
                  <div className="text-center py-12 text-gray-400">
                    <UserCog className="h-12 w-12 mx-auto mb-3 opacity-50" />
                    <p>Select a role to manage permissions</p>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* Permissions Matrix Tab */}
        <TabsContent value="permissions">
          <Card>
            <CardHeader>
              <CardTitle>Permissions Matrix</CardTitle>
              <CardDescription>Overview of all roles and their permissions</CardDescription>
            </CardHeader>
            <CardContent className="p-0 overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="sticky left-0 bg-white">Permission</TableHead>
                    {roles.map((role) => (
                      <TableHead key={role.id} className="text-center min-w-[100px]">
                        {role.name}
                      </TableHead>
                    ))}
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {permissions.map((perm) => (
                    <TableRow key={perm.id}>
                      <TableCell className="sticky left-0 bg-white font-mono text-xs">
                        {perm.perm_key}
                      </TableCell>
                      {roles.map((role) => {
                        const hasPermission = role.permissions?.some(p => p.id === perm.id);
                        return (
                          <TableCell key={role.id} className="text-center">
                            {hasPermission ? (
                              <Check className="h-4 w-4 text-green-500 mx-auto" />
                            ) : (
                              <X className="h-4 w-4 text-gray-300 mx-auto" />
                            )}
                          </TableCell>
                        );
                      })}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Create/Edit Role Dialog */}
      <Dialog open={showRoleDialog} onOpenChange={setShowRoleDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{editingRole ? 'Edit Role' : 'Create Role'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <label className="text-sm font-medium">Role Name</label>
              <Input
                value={roleForm.name}
                onChange={(e) => setRoleForm({ ...roleForm, name: e.target.value })}
                placeholder="e.g., support_agent"
              />
            </div>
            <div>
              <label className="text-sm font-medium">Description</label>
              <Input
                value={roleForm.description}
                onChange={(e) => setRoleForm({ ...roleForm, description: e.target.value })}
                placeholder="What can this role do?"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowRoleDialog(false)}>Cancel</Button>
            <Button onClick={handleSaveRole}>{editingRole ? 'Update' : 'Create'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
